import React from 'react';
// Using standard <a> tags
import { X, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PublicFooter = () => {
    const { t } = useTranslation('marketing');
    const currentYear = new Date().getFullYear();

    return (
        <footer className="bg-neutral-950 border-t border-neutral-800 mt-auto">
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Brand */}
                    <div className="space-y-4">
                        <h3 className="text-xl font-black text-white uppercase italic tracking-wider">
                            GymTracker<span className="text-red-500">.pro</span>
                        </h3>
                        <p className="text-sm text-neutral-400">
                            {t('footer.brand_tagline')}
                        </p>
                    </div>

                    {/* Producto */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-black text-white uppercase tracking-wider">{t('footer.product')}</h4>
                        <ul className="space-y-2">
                            <li>
                                <a href="/resources" className="text-sm text-neutral-400 hover:text-red-500 transition-colors">
                                    {t('footer.resources')}
                                </a>
                            </li>
                            <li>
                                <a href="/about" className="text-sm text-neutral-400 hover:text-red-500 transition-colors">
                                    {t('footer.about')}
                                </a>
                            </li>
                            <li>
                                <a href="/contact" className="text-sm text-neutral-400 hover:text-red-500 transition-colors">
                                    {t('footer.contact')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Legal */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-black text-white uppercase tracking-wider">{t('footer.legal')}</h4>
                        <ul className="space-y-2">
                            <li>
                                <a href="/privacy" className="text-sm text-neutral-400 hover:text-red-500 transition-colors">
                                    {t('footer.privacy')}
                                </a>
                            </li>
                            <li>
                                <a href="/terms" className="text-sm text-neutral-400 hover:text-red-500 transition-colors">
                                    {t('footer.terms')}
                                </a>
                            </li>
                            <li>
                                <a href="/cookies" className="text-sm text-neutral-400 hover:text-red-500 transition-colors">
                                    {t('footer.cookies')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Social */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-black text-white uppercase tracking-wider">{t('footer.follow_us')}</h4>
                        <div className="flex gap-3">
                            <a
                                href="https://tiktok.com/@gymtrackerpro"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Síguenos en TikTok"
                                className="p-2 bg-neutral-900 hover:bg-red-900/20 border border-neutral-800 hover:border-red-900/50 rounded-lg transition-colors"
                            >
                                <svg className="text-neutral-400 hover:text-red-500 transition-colors" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                                </svg>
                            </a>
                            <a
                                href="https://twitter.com/gymtrackerpro"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Síguenos en X (Twitter)"
                                className="p-2 bg-neutral-900 hover:bg-red-900/20 border border-neutral-800 hover:border-red-900/50 rounded-lg transition-colors"
                            >
                                <X className="text-neutral-400 hover:text-red-500" size={20} />
                            </a>
                            <a
                                href="https://instagram.com/gymtrackerpro"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Síguenos en Instagram"
                                className="p-2 bg-neutral-900 hover:bg-red-900/20 border border-neutral-800 hover:border-red-900/50 rounded-lg transition-colors"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-neutral-400 hover:text-red-500"
                                >
                                    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                                    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                                </svg>
                            </a>
                            <a
                                href="mailto:info@gymtracker.pro"
                                aria-label="Envíanos un correo electrónico"
                                className="p-2 bg-neutral-900 hover:bg-red-900/20 border border-neutral-800 hover:border-red-900/50 rounded-lg transition-colors"
                            >
                                <Mail className="text-neutral-400 hover:text-red-500" size={20} />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <div className="mt-12 pt-8 border-t border-neutral-800">
                    <p className="text-center text-sm text-neutral-400">
                        © {currentYear} GymTracker Pro. {t('footer.copyright')}.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default PublicFooter;
